import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { registerDoctor } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    license_number: "",
    specialty: "",
    whatsapp: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const r = await registerDoctor(form);
    setSubmitting(false);
    if (r.ok) {
      toast.success("✓");
      navigate("/dashboard/verification", { replace: true });
    } else setError(r.error);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="register-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-24">
      <div className="overline mb-3">{t("reg.overline")}</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-tight">{t("reg.title")}</h1>
      <p className="mt-4 text-sm text-muted-foreground max-w-xl leading-relaxed">{t("reg.subtitle")}</p>

      <Button
        type="button"
        variant="outline"
        className="w-full mt-8 sm:mt-10 rounded-md h-12"
        onClick={googleLogin}
        data-testid="google-register-button"
      >
        {t("login.google")}
      </Button>

      <div className="flex items-center gap-3 my-5 sm:my-6">
        <div className="flex-1 h-px bg-border" />
        <span className="overline">{t("login.or")}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name">{t("reg.name")}</Label>
          <Input id="name" required value={form.name} onChange={update("name")}
                 className="mt-2 h-11" data-testid="register-name" />
        </div>
        <div>
          <Label htmlFor="email">{t("reg.email")}</Label>
          <Input id="email" type="email" required value={form.email} onChange={update("email")}
                 className="mt-2 h-11" data-testid="register-email" />
        </div>
        <div>
          <Label htmlFor="password">{t("reg.password")}</Label>
          <Input id="password" type="password" required minLength={8}
                 value={form.password} onChange={update("password")}
                 className="mt-2 h-11" data-testid="register-password" />
        </div>
        <div>
          <Label htmlFor="specialty">{t("reg.specialty")}</Label>
          <Input id="specialty" required value={form.specialty} onChange={update("specialty")}
                 placeholder="Endocrinología, Medicina interna, …"
                 className="mt-2 h-11" data-testid="register-specialty" />
        </div>
        <div>
          <Label htmlFor="whatsapp">{t("reg.whatsapp")}</Label>
          <Input id="whatsapp" required type="tel" inputMode="tel" value={form.whatsapp} onChange={update("whatsapp")}
                 placeholder="+1 809 555 1234"
                 className="mt-2 h-11" data-testid="register-whatsapp" />
          <p className="text-xs text-muted-foreground mt-1">{t("reg.whatsapp.hint")}</p>
        </div>
        <div>
          <Label htmlFor="license">{t("reg.license")}</Label>
          <Input id="license" value={form.license_number} onChange={update("license_number")}
                 className="mt-2 h-11" data-testid="register-license" />
        </div>
        {error && <div data-testid="register-error" className="text-sm text-destructive">{error}</div>}
        <Button type="submit" disabled={submitting} className="w-full h-12 rounded-md" data-testid="register-submit">
          {submitting ? t("reg.submitting") : t("reg.submit")}
        </Button>
      </form>

      <div className="mt-8 text-sm text-muted-foreground">
        {t("reg.has")}{" "}
        <Link to="/login" className="underline text-foreground">{t("reg.has.link")}</Link>
      </div>
    </div>
  );
}
